import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  fetchNotifications, markNotificationsRead,
  subscribeToNotifications
} from '@munawb/supabase';
import type { NotificationRow } from '@munawb/types';

export const notifKeys = {
  all:  (userId: string) => ['notifications', userId] as const,
};

export function useNotifications(
  user_id: string,
  user_dept: string | null,
  unread_only = false
) {
  const qc = useQueryClient();

  // جلب الإشعارات
  const query = useQuery({
    queryKey: notifKeys.all(user_id),
    queryFn:  () => fetchNotifications(user_id, user_dept, unread_only),
    enabled:  !!user_id,
    staleTime: 10_000,
    refetchInterval: 30_000,   // fallback polling إذا انقطع WebSocket
  });

  // Realtime subscription
  useEffect(() => {
    if (!user_id) return;
    const channel = subscribeToNotifications(user_id, (notif: NotificationRow) => {
      qc.setQueryData(
        notifKeys.all(user_id),
        (old: NotificationRow[] | undefined) => [notif, ...(old ?? [])]
      );
    });
    return () => { void channel.unsubscribe(); };
  }, [user_id, qc]);

  const unreadCount = query.data?.filter((n) => !n.is_read).length ?? 0;

  return { ...query, unreadCount };
}

export function useMarkNotificationsRead(user_id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => markNotificationsRead(ids),
    onSuccess: (_result, ids) => {
      qc.setQueryData(
        notifKeys.all(user_id),
        (old: NotificationRow[] | undefined) =>
          old?.map((n) => ids.includes(n.id) ? { ...n, is_read: true } : n)
      );
    },
  });
}
